import clsx from "clsx";
import { memo, useMemo } from "react";
import { MdClose, MdDeselect, MdSelectAll } from "react-icons/md";
import CheckBox from "src/components/CheckBox/CheckBox";
import Modal from "src/components/Modal/Modal";
import useModalsStore from "src/store/modalsStore";
import { useTranslation } from "react-i18next";
import SettingsPerDeviceInfoBlock from "../InfoBlock/SettingsPerDeviceInfoBlock/SettingsPerDeviceInfoBlock";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import useUiPreferences from "src/store/useUiPreferences";
import { EButtonKind } from "../Button/Button.types";

const TransactionSettingsModal: React.FC = () => {
  const { t } = useTranslation();
  const setUiPreferences = useUiPreferences((s) => s.setUiPreferences);
  const transactionUiPreferences = useUiPreferences((s) => s.transaction);

  const { setShowTransactionSettingsModal } = useModalsStore();

  const onClose = () => setShowTransactionSettingsModal(false);

  const isAllSelected = useMemo(() => {
    return (
      transactionUiPreferences.showDiscountField &&
      transactionUiPreferences.showDetails &&
      transactionUiPreferences.showNotes &&
      transactionUiPreferences.showTaxesField &&
      transactionUiPreferences.showVoucherField &&
      transactionUiPreferences.showDocumentNumberField
    );
  }, [
    transactionUiPreferences.showDiscountField,
    transactionUiPreferences.showDetails,
    transactionUiPreferences.showNotes,
    transactionUiPreferences.showTaxesField,
    transactionUiPreferences.showVoucherField,
    transactionUiPreferences.showDocumentNumberField,
  ]);

  const selectAll = () => {
    setUiPreferences({
      transaction: {
        showDiscountField: true,
        showTaxesField: true,
        showVoucherField: true,
        showNotes: true,
        showDetails: true,
        showDocumentNumberField: true,
      },
    });
  };

  const deselectAll = () => {
    setUiPreferences({
      transaction: {
        showDiscountField: false,
        showTaxesField: false,
        showVoucherField: false,
        showNotes: false,
        showDetails: false,
        showDocumentNumberField: false,
      },
    });
  };

  return (
    <Modal
      onEscape={() => onClose()}
      className="md:!max-w-[580px] md:!min-w-[280px] md:w-full"
    >
      <Modal.Header title="Campos opcionales" onClose={() => onClose()} />
      <Modal.Body>
        <div className={clsx(["flex", "w-full", "flex-col", "gap-4"])}>
          <SettingsPerDeviceInfoBlock />
          <div
            className={clsx([
              "grid",
              "grid-cols-1",
              "md:grid-cols-2",
              "gap-3",
              "md:gap-4",
            ])}
          >
            <CheckBox
              label="Número de documento"
              onChange={(isChecked: boolean) => {
                setUiPreferences({
                  transaction: {
                    showDocumentNumberField: isChecked,
                  },
                });
              }}
              checked={transactionUiPreferences.showDocumentNumberField}
              description="Mostrar el campo para número de factura, recibo, etc."
            />
            <CheckBox
              label="Descuento"
              onChange={(isChecked: boolean) => {
                setUiPreferences({
                  transaction: {
                    showDiscountField: isChecked,
                  },
                });
              }}
              checked={transactionUiPreferences.showDiscountField}
              description="Mostrar el campo para descuento y la opción para calcular."
            />
            <CheckBox
              label="Impuestos"
              onChange={(isChecked: boolean) => {
                setUiPreferences({
                  transaction: {
                    showTaxesField: isChecked,
                  },
                });
              }}
              checked={transactionUiPreferences.showTaxesField}
              description="Mostrar el campo para impuestos y la opción para calcular."
            />
            <CheckBox
              label="Notas"
              onChange={(isChecked: boolean) => {
                setUiPreferences({
                  transaction: {
                    showNotes: isChecked,
                  },
                });
              }}
              checked={transactionUiPreferences.showNotes}
              description="Mostrar el campo para agregar notas personalizadas."
            />
            <CheckBox
              label="Número de comprobante"
              onChange={(isChecked: boolean) => {
                setUiPreferences({
                  transaction: {
                    showVoucherField: isChecked,
                  },
                });
              }}
              checked={transactionUiPreferences.showVoucherField}
              description="Mostrar el campo para número de comprobante bancario, junto al método de pago."
            />
            {/* <CheckBox
              label="Transacciones detalladas"
              onChange={(isChecked: boolean) => {
                setUiPreferences({
                  transaction: {
                    showDetails: isChecked,
                  },
                });
              }}
              checked={transactionUiPreferences.showDetails}
              description="Mostrar la opción para crear transacciones detalladas., que incluye cantidad, precio unitario, etc."
            /> */}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer
        leftContent={
          <ButtonGroup
            buttons={[
              {
                onClick: () => selectAll(),
                tooltip: "Seleccionar todo",
                icon: <MdSelectAll />,
                hide: isAllSelected,
              },
              {
                onClick: () => deselectAll(),
                tooltip: "Deseleccionar todo",
                icon: <MdDeselect />,
                hide: !isAllSelected,
              },
            ]}
          />
        }
        actions={[
          {
            text: t("common:buttons.close"),
            onClick: onClose,
            kind: EButtonKind.NORMAL,
            icon: <MdClose />,
          },
        ]}
      />
    </Modal>
  );
};

export default memo(TransactionSettingsModal);
